import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { useAITutor, type EventGuidanceData } from "@/hooks/useAITutor";

interface Props {
  eventTitle: string;
  eventDescription: string;
  eventType: "money_event" | "life_event";
  options?: { label: string; outcome_hint?: string }[];
}

const QUICK_PROMPTS = [
  "What's the tradeoff here?",
  "What should I think about?",
  "Explain these choices",
];

const FALLBACK: EventGuidanceData = {
  guidance: "Consider the short-term and long-term impact of each option. Which choice protects your financial stability while solving the problem?",
  strategic_hint: "Think about what each option costs you — not just in money, but in time and future flexibility.",
  concept_explanation: "Financial decisions often involve balancing immediate needs against long-term goals.",
};

export default function AIEventGuidance({ eventTitle, eventDescription, eventType, options }: Props) {
  const { loading, getEventGuidance } = useAITutor();
  const [response, setResponse] = useState<EventGuidanceData | null>(null);
  const [showPanel, setShowPanel] = useState(false);
  const [useFallback, setUseFallback] = useState(false);

  const handleAsk = async (question: string) => {
    setUseFallback(false);
    setShowPanel(true);
    const result = await getEventGuidance({
      eventTitle,
      eventText: `${eventDescription} — Learner asks: "${question}"`,
      options,
    });
    if (result.fallback || !result.data) {
      setUseFallback(true);
    } else {
      setResponse(result.data);
    }
  };

  const content = response || (useFallback ? FALLBACK : null);

  if (!showPanel) {
    return (
      <button
        onClick={() => setShowPanel(true)}
        className="flex items-center gap-1.5 text-[10px] text-muted-foreground/70 hover:text-primary transition-colors mx-auto mt-1"
      >
        <Sparkles className="w-3 h-3" />
        <span>Need help deciding?</span>
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-primary/15 bg-primary/5 p-3 space-y-2.5 animate-fade-in">
      <div className="flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-primary" />
        <span className="text-[10px] font-semibold text-primary">AI Guide</span>
      </div>

      {!content && !loading && (
        <div className="flex flex-wrap gap-1.5">
          {QUICK_PROMPTS.map((q) => (
            <button
              key={q}
              onClick={() => handleAsk(q)}
              className="text-[10px] px-2 py-1 rounded-full border border-border bg-card/50 text-muted-foreground hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-1.5 py-2">
          <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
          <span className="text-[10px] text-muted-foreground">Thinking about this...</span>
        </div>
      )}

      {content && !loading && (
        <div className="space-y-1.5">
          <p className="text-xs text-foreground leading-relaxed">{content.guidance}</p>
          <p className="text-[10px] text-muted-foreground">{content.strategic_hint}</p>
          <p className="text-[10px] text-muted-foreground/70 italic">{content.concept_explanation}</p>
        </div>
      )}
    </div>
  );
}
